import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, File as FileIcon, Send, Lock, UploadCloud, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import api, { downloadFile, formatApiError, STATUS_LABELS } from "../../lib/api";
import { StatusBadge, CaseTimeline } from "../../components/StatusTools";
import { StlViewerButton } from "../../components/STLViewer";
import { FilePreviewButton } from "../../components/FilePreview";
import { useAuth } from "../../context/AuthContext";

export default function AdminCaseDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const base = isAdmin ? "/admin" : "/planner";
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [assignee, setAssignee] = useState("");
  const [planners, setPlanners] = useState([]);
  const [msg, setMsg] = useState("");
  const [msgInternal, setMsgInternal] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [invAmount, setInvAmount] = useState("");
  const [invCurrency, setInvCurrency] = useState("USD");
  const planRef = useRef(null);

  const load = () => api.get(`/cases/${id}`).then((r) => {
    setData(r.data);
    setStatus(r.data.status);
    setAssignee(r.data.assigned_planner_id || "");
    if (r.data.invoice) {
      setInvAmount(String(r.data.invoice.amount));
      setInvCurrency(r.data.invoice.currency);
    }
  }).catch(() => setData(false));
  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps -- load is stable per id; re-runs on id change only
  }, [id]);
  useEffect(() => {
    if (isAdmin) api.get("/admin/planners").then((r) => setPlanners(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps -- api is a stable module import
  }, [isAdmin]);

  const run = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const updateStatus = () => run(
    () => api.patch(`/admin/cases/${id}`, { status, status_note: statusNote }),
    `Status updated to ${STATUS_LABELS[status]}`
  ).then(() => setStatusNote(""));

  const saveAssignee = (v) => {
    const pid = v === "unassigned" ? "" : v;
    run(() => api.patch(`/admin/cases/${id}`, { assigned_planner_id: pid }), pid ? "Planner assigned" : "Planner unassigned")
      .then(() => setAssignee(pid));
  };

  const sendMessage = (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    run(() => api.post(`/cases/${id}/messages`, { body: msg.trim(), internal: msgInternal }),
      msgInternal ? "Internal note added to thread" : "Reply sent to doctor").then(() => { setMsg(""); setMsgInternal(false); });
  };

  const addNote = () => {
    if (!note.trim()) return;
    run(() => api.post(`/admin/cases/${id}/notes`, { body: note.trim() }), "Internal note saved").then(() => setNote(""));
  };

  const sendInvoice = () => {
    const amt = parseFloat(invAmount);
    if (!amt || amt <= 0) { toast.error("Enter a valid invoice amount"); return; }
    run(() => api.post(`/admin/cases/${id}/invoice`, { amount: amt, currency: invCurrency }), "Invoice sent to the doctor");
  };

  const uploadPlan = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    await run(() => api.post(`/admin/cases/${id}/plan`, fd, { headers: { "Content-Type": "multipart/form-data" } }),
      "Treatment plan delivered to the doctor");
  };

  if (data === null) return <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-sky-600 border-t-transparent animate-spin" /></div>;
  if (data === false) return <p className="py-24 text-center text-slate-500">Case not found.</p>;

  return (
    <div data-testid="admin-case-detail">
      <Link to={base} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 mb-6" data-testid="admin-case-back">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to cases
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display font-black text-3xl tracking-tight font-mono" data-testid="admin-case-id">{data.case_id}</h1>
            <StatusBadge status={data.status} />
          </div>
          <p className="text-sm text-slate-500 mt-1.5">
            {data.client_name} · {data.clinic_name || "No clinic"} · {data.patient_ref} · {data.case_type} · {data.arch} arch
          </p>
        </div>
        <Button onClick={() => planRef.current?.click()} disabled={busy} data-testid="admin-upload-plan-btn"
          className="rounded-full bg-emerald-600 hover:bg-emerald-700 font-bold">
          <UploadCloud className="h-4 w-4 mr-2" /> Upload Final Plan
        </Button>
        <input ref={planRef} type="file" className="hidden" data-testid="admin-plan-file-input" onChange={(e) => { uploadPlan(e.target.files?.[0]); e.target.value = ""; }} />
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Clinical brief</h2>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div><dt className="text-slate-400 text-xs mb-1">Software preference</dt><dd className="font-semibold">{data.software_preference}</dd></div>
              <div><dt className="text-slate-400 text-xs mb-1">Urgency</dt><dd className="font-semibold capitalize">{data.urgency}</dd></div>
              <div className="sm:col-span-2"><dt className="text-slate-400 text-xs mb-1">Treatment goals</dt><dd className="leading-relaxed whitespace-pre-wrap">{data.treatment_goals}</dd></div>
              {data.clinical_notes && <div className="sm:col-span-2"><dt className="text-slate-400 text-xs mb-1">Clinical notes</dt><dd className="leading-relaxed whitespace-pre-wrap">{data.clinical_notes}</dd></div>}
            </dl>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Files ({data.files.length})</h2>
            {data.files.length === 0 ? <p className="text-sm text-slate-400">No files uploaded.</p> : (
              <ul className="space-y-2">
                {data.files.map((f) => (
                  <li key={f.id} className={`flex items-center justify-between border rounded-lg px-4 py-2.5 text-sm ${f.kind === "plan" ? "border-emerald-200 bg-emerald-50/60" : "border-slate-100 bg-slate-50"}`}>
                    <span className="flex items-center gap-2 min-w-0">
                      <FileIcon className={`h-4 w-4 shrink-0 ${f.kind === "plan" ? "text-emerald-600" : "text-sky-600"}`} />
                      <span className="truncate font-medium">{f.filename}</span>
                      <span className="text-xs text-slate-400 shrink-0">{f.kind} · {(f.size / 1048576).toFixed(1)} MB</span>
                    </span>
                    <span className="flex items-center shrink-0">
                      <StlViewerButton file={f} />
                      <FilePreviewButton file={f} />
                      <button onClick={() => downloadFile(f.id, f.filename).catch((e) => toast.error(formatApiError(e)))} data-testid={`admin-file-download-${f.id}`}
                        className="ml-3 inline-flex items-center gap-1 text-xs font-bold text-sky-600 hover:text-sky-800">
                        <Download className="h-3.5 w-3.5" /> Download
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Conversation</h2>
            <div className="space-y-4 mb-5 max-h-80 overflow-y-auto pr-1" data-testid="admin-case-messages">
              {data.messages.length === 0 && <p className="text-sm text-slate-400">No messages yet.</p>}
              {data.messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender_role !== "client" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${m.internal ? "bg-amber-50 border border-amber-200 text-amber-900" : m.sender_role !== "client" ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-800"}`}>
                    <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 flex items-center gap-1 ${m.internal ? "text-amber-500" : m.sender_role !== "client" ? "text-sky-200" : "text-slate-400"}`}>
                      {m.internal && <Lock className="h-3 w-3" />} {m.sender_name} · {new Date(m.created_at).toLocaleString()}
                    </p>
                    <p className="leading-relaxed whitespace-pre-wrap">{m.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={sendMessage} className="space-y-3" data-testid="admin-message-form">
              <Textarea rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="admin-message-input"
                placeholder="Reply to the doctor, or toggle internal for a private note…" className="resize-none" />
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-500 cursor-pointer">
                  <input type="checkbox" checked={msgInternal} onChange={(e) => setMsgInternal(e.target.checked)} data-testid="admin-internal-toggle"
                    className="h-4 w-4 rounded border-slate-300 text-amber-500 focus:ring-amber-400" />
                  Internal only (hidden from doctor)
                </label>
                <Button type="submit" disabled={busy || !msg.trim()} data-testid="admin-message-send-btn" className="rounded-full bg-sky-600 hover:bg-sky-700 font-bold h-10">
                  <Send className="h-4 w-4 mr-2" /> {msgInternal ? "Add note" : "Send reply"}
                </Button>
              </div>
            </form>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="admin-status-panel">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Update status</h2>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-11" data-testid="admin-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea rows={2} value={statusNote} onChange={(e) => setStatusNote(e.target.value)} data-testid="admin-status-note-input"
              placeholder="Optional note to the doctor (included in their email)…" className="mt-3 resize-none" />
            <Button onClick={updateStatus} disabled={busy || status === data.status} data-testid="admin-status-update-btn"
              className="mt-3 w-full rounded-full bg-sky-600 hover:bg-sky-700 font-bold">
              {busy ? "Updating…" : "Update status & notify doctor"}
            </Button>
          </div>

          {isAdmin && (
            <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="admin-billing-panel">
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Billing</h2>
              {data.invoice && (
                <div className={`mb-4 rounded-lg border px-4 py-3 text-sm flex items-center justify-between ${data.invoice.status === "paid" ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/60"}`} data-testid="admin-invoice-status">
                  <span className="font-bold">{data.invoice.currency} {Number(data.invoice.amount).toFixed(2)}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${data.invoice.status === "paid" ? "text-emerald-600" : "text-amber-600"}`}>
                    {data.invoice.status}{data.invoice.payment_method ? ` · ${data.invoice.payment_method}` : ""}
                  </span>
                </div>
              )}
              {data.invoice?.status !== "paid" && (
                <>
                  <div className="flex gap-2">
                    <Input type="number" min="1" step="0.01" value={invAmount} onChange={(e) => setInvAmount(e.target.value)}
                      data-testid="admin-invoice-amount-input" placeholder="Amount" className="h-11" />
                    <Select value={invCurrency} onValueChange={setInvCurrency}>
                      <SelectTrigger className="h-11 w-28" data-testid="admin-invoice-currency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["USD", "INR", "EUR", "GBP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={sendInvoice} disabled={busy || !invAmount} data-testid="admin-invoice-send-btn"
                    className="mt-3 w-full rounded-full bg-amber-500 hover:bg-amber-600 font-bold">
                    {data.invoice ? "Update invoice & notify doctor" : "Create invoice & notify doctor"}
                  </Button>
                  <p className="text-[11px] text-slate-400 mt-2 leading-snug">The doctor must pay before the final plan can be downloaded.</p>
                </>
              )}
            </div>
          )}

          {isAdmin && (
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Assign planner</h2>
              <Select value={assignee || "unassigned"} onValueChange={saveAssignee} disabled={busy}>
                <SelectTrigger className="h-11" data-testid="admin-assignee-select"><SelectValue placeholder="Select a planner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {planners.filter((p) => p.active !== false).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.active_cases} active</SelectItem>)}
                </SelectContent>
              </Select>
              <Link to="/admin/planners" data-testid="admin-manage-planners-link"
                className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-sky-600 hover:text-sky-800">
                <UserPlus className="h-3.5 w-3.5" /> Manage planner accounts
              </Link>
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Internal notes</h2>
            <ul className="space-y-3 mb-4">
              {(data.internal_notes || []).length === 0 && <p className="text-sm text-slate-400">No internal notes yet.</p>}
              {(data.internal_notes || []).map((n) => (
                <li key={n.id} className="text-sm border border-amber-100 bg-amber-50/60 rounded-lg p-3">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-500 mb-1">{n.by} · {new Date(n.created_at).toLocaleString()}</p>
                  <p className="text-amber-900 leading-relaxed">{n.body}</p>
                </li>
              ))}
            </ul>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-note-input" placeholder="Private note for the team…" className="resize-none" />
            <Button onClick={addNote} disabled={busy || !note.trim()} data-testid="admin-note-save-btn" variant="outline" className="mt-3 w-full rounded-full font-bold border-slate-300">
              Save internal note
            </Button>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-5">Timeline</h2>
            <CaseTimeline history={data.status_history} />
          </div>
        </div>
      </div>
    </div>
  );
}
